import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { environment } from 'environments/environment';
import type {
  KeycloakProfile,
  KeycloakTokenParsed,
  KeycloakUserInfo,
} from 'keycloak-js';
import Keycloak from 'keycloak-js';
import { BehaviorSubject, Subject } from 'rxjs';
import { AppComfirmLogoutComponent } from '../app-confirm/app-confirm-logout.component';
import {
  buildSilentCheckSsoRedirectUri,
  parseKeycloakAuthority,
  resolveAuthUrl,
} from './keycloak-auth.config';

@Injectable({ providedIn: 'root' })
export class KeycloakAuthService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly tokenStorageKey = 'token';
  private readonly userInfoStorageKey = 'user';
  private readonly loginHintStorageKey = 'login_hint';
  private readonly skipAutoLoginStorageKey = 'skip-auto-login-once';
  private readonly postLoginRedirectStorageKey = 'post-login-redirect';
  private readonly idleActivityStorageKey = 'keycloak-idle-activity';
  private readonly logoutEventStorageKey = 'keycloak-logout-event';
  private readonly activityEvents = [
    'mousedown',
    'mousemove',
    'keydown',
    'scroll',
    'touchstart',
    'click',
    'wheel',
    'pointerdown',
  ] as const;
  private sessionIdleWarningTimeoutId: number | null = null;
  private sessionIdleDialogRef: MatDialogRef<AppComfirmLogoutComponent> | null =
    null;
  private sessionIdleLogoutTimeoutId: number | null = null;
  private readonly isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  private readonly dialog = inject(MatDialog);
  private readonly readySubject = new BehaviorSubject<boolean>(false);
  private readonly sessionTerminatedSubject = new Subject<void>();
  private keycloak: Keycloak | null = null;
  private initializePromise: Promise<boolean> | null = null;
  private explicitLogoutRequested = false;
  private initialLoadCompleted = false;
  private userProfile: KeycloakProfile | null = null;
  private userInfoCache: Promise<KeycloakUserInfo | null> | null = null;
  private readonly keycloakRuntimeConfig = parseKeycloakAuthority(
    environment.keycloak.authority,
    environment.keycloak.clientId,
  );

  private lastActivityRefreshAt = 0;
  private readonly activityRefreshMinIntervalMs = 30_000;
  private readonly tokenRefreshMinIntervalMs = 30_000;
  private tokenRefreshInFlight: Promise<string | null> | null = null;
  private lastTokenRefreshAttemptAt = 0;
  private refreshTokenExpiresAt: number | null = null;
  private refreshTokenLifetimeSeconds: number | null = null;

  private readonly onUserActivity = (): void => {
    this.persistLastActivity();
    this.resetIdleTimer();
    this.extendSessionOnActivity();
  };

  readonly isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  readonly ready$ = this.readySubject.asObservable();
  readonly sessionTerminated$ = this.sessionTerminatedSubject.asObservable();

  async initialize(): Promise<boolean> {
    if (this.readySubject.value) {
      return this.isAuthenticatedSubject.value;
    }

    if (this.initializePromise) {
      return this.initializePromise;
    }

    if (!isPlatformBrowser(this.platformId)) {
      this.readySubject.next(true);
      return false;
    }

    // SSO may navigate away during bootstrap, before route guards run.
    // Preserve direct links now, without replacing them on the callback.
    const { pathname, search, hash } = window.location;
    if (pathname !== '/' && !pathname.startsWith('/sessions/') &&
        !pathname.startsWith('/secure/') && pathname !== '/forbidden-403') {
      this.setPostLoginRedirectUrl(`${pathname}${search}${hash}`);
    }

    this.initializePromise = this.bootstrapKeycloak();
    this.listenForCrossTabLogout();
    this.startIdleMonitor();
    return this.initializePromise;
  }

  async isAuthenticated(): Promise<boolean> {
    await this.initialize();
    return this.isAuthenticatedSubject.value;
  }

  async login(
    extraOptions?: Partial<Keycloak.KeycloakLoginOptions>,
  ): Promise<void> {
    await this.initialize();
    const loginHint = this.consumeLoginHint();
    this.clearStoredTokens();

    await this.requireKeycloak().login({
      ...extraOptions,
      redirectUri: resolveAuthUrl(environment.keycloak.redirectUri),
      scope: environment.keycloak.scope,
      ...(loginHint ? { loginHint } : {}),
    });
  }

  private persistLoginHint(loginHint: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const normalized = loginHint.trim().toLowerCase();
    if (!normalized) {
      return;
    }

    window.sessionStorage.setItem(this.loginHintStorageKey, normalized);
  }

  private consumeLoginHint(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    const value = window.sessionStorage.getItem(this.loginHintStorageKey);
    if (!value) {
      return null;
    }

    window.sessionStorage.removeItem(this.loginHintStorageKey);
    return value;
  }

  /**
   * logout() is frequently called "fire and forget" (e.g. from the
   * expiry-warning dialog's "Sign out now" button) without an await/catch
   * on the caller's side. That means this method must never throw
   * synchronously or reject before it reaches keycloak.logout() -
   * otherwise the sign-out button silently does nothing.
   */
  async logout(explicit = true): Promise<void> {
    const storedUser = this.getStoredUserInfo();

    await this.initialize();
    this.explicitLogoutRequested = explicit;
    this.clearIdleTimers();
    this.clearSessionIdleWarning();
    this.clearStoredTokens();
    this.markSkipAutoLoginOnce();
    this.clearStoredUserInfo();
    this.userInfoCache = null;
    this.tokenRefreshInFlight = null;
    this.lastTokenRefreshAttemptAt = 0;
    this.broadcastLogout();

    const keycloak = this.requireKeycloak();
    const postLogoutUrl = this.getSafeSameOriginUrl(
      environment.keycloak.postLogoutRedirectUri,
    );

    const preferredUsername = storedUser?.userInfo?.preferred_username;
    if (preferredUsername) {
      this.persistLoginHint(preferredUsername);
    }

    try {
      await keycloak.updateToken(30);
    } catch (e) {
      console.error('Error updating token before logout:', e);
    }

    keycloak.logout({
      redirectUri: postLogoutUrl,
    });
  }

  async changePassword(): Promise<void> {
    const storedUser = this.getStoredUserInfo();
    await this.initialize();

    const keycloak = this.requireKeycloak();
    const loginHint = storedUser?.userInfo?.preferred_username ?? '';
    this.persistLoginHint(loginHint);
    await keycloak.login({
      action: 'UPDATE_PASSWORD',
      ...(loginHint ? { loginHint } : {}),
    });
  }

  consumeSkipAutoLoginOnce(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    const value = window.sessionStorage.getItem(this.skipAutoLoginStorageKey);
    if (value !== '1') {
      return false;
    }

    window.sessionStorage.removeItem(this.skipAutoLoginStorageKey);
    return true;
  }

  async getValidToken(minValiditySeconds = 30): Promise<string | null> {
    return this.refreshAccessToken(minValiditySeconds);
  }

  /**
   * Forces a real call to the Keycloak token endpoint, regardless of how
   * much time is left on the current access token.
   */
  async forceRefreshToken(): Promise<string | null> {
    return this.refreshAccessToken(-1, true);
  }

  async getTokenClaims(): Promise<KeycloakTokenParsed | null> {
    await this.initialize();
    return this.keycloak?.tokenParsed ?? this.keycloak?.idTokenParsed ?? null;
  }

  async getUserProfile(): Promise<KeycloakProfile | null> {
    await this.initialize();

    if (this.userProfile || !this.keycloak?.authenticated) {
      return this.userProfile;
    }

    try {
      this.userProfile = await this.keycloak.loadUserProfile();
      this.persistUserInfo();
      return this.userProfile;
    } catch {
      return null;
    }
  }

  async getUserInfo(): Promise<KeycloakUserInfo | null> {
    await this.initialize();

    if (!this.keycloak?.authenticated) {
      return null;
    }

    if (!this.userInfoCache) {
      this.userInfoCache = this.keycloak
        .loadUserInfo()
        .then((userInfo) => {
          if (userInfo) {
            this.persistUserInfo();
          }
          return userInfo;
        })
        .catch(() => null);
    }

    return this.userInfoCache;
  }

  setPostLoginRedirectUrl(url: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const safeUrl = this.getSafeAppRoute(url);
    if (!safeUrl || this.isAuthUtilityRoute(safeUrl)) {
      return;
    }

    window.sessionStorage.setItem(this.postLoginRedirectStorageKey, safeUrl);
  }

  consumePostLoginRedirectUrl(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    const value = window.sessionStorage.getItem(
      this.postLoginRedirectStorageKey,
    );
    window.sessionStorage.removeItem(this.postLoginRedirectStorageKey);

    return value ? this.getSafeAppRoute(value) : null;
  }

  /**
   * Get stored user information from localStorage
   */
  getStoredUserInfo(): {
    profile?: KeycloakProfile;
    userInfo?: KeycloakUserInfo;
  } | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    const raw = window.localStorage.getItem(this.userInfoStorageKey);
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  /**
   * Clear stored user information from localStorage
   */
  clearStoredUserInfo(): void {
    window.localStorage.removeItem('id');
    window.localStorage.removeItem(this.userInfoStorageKey);
  }

  private async bootstrapKeycloak(): Promise<boolean> {
    // Never restore credentials persisted by older application versions.
    window.localStorage.removeItem(this.tokenStorageKey);
    window.localStorage.removeItem('logto_refresh_token');

    const { default: Keycloak } = await import('keycloak-js');
    const keycloak = new Keycloak(
      parseKeycloakAuthority(
        environment.keycloak.authority,
        environment.keycloak.clientId,
      ),
    );

    this.keycloak = keycloak;
    this.bindEventHandlers(keycloak);

    const enableSsoCheck = environment.keycloak.enableSsoCheck ?? true;
    const enableSilentSso = environment.keycloak.enableSilentSso ?? true;
    const allowIframeSessionChecks =
      environment.keycloak.enableIframeSessionChecks ?? false;
    const storedTokens = this.readStoredTokens();

    if (storedTokens) {
      keycloak.token = storedTokens.token ?? null;
      keycloak.refreshToken = storedTokens.refreshToken ?? null;
      keycloak.idToken = storedTokens.idToken ?? null;
      keycloak.timeSkew = storedTokens.timeSkew ?? null;
    }

    try {
      const initOptions: Keycloak.KeycloakInitOptions = {
        onLoad: enableSsoCheck ? 'check-sso' : undefined,
        flow: 'standard',
        responseMode: 'fragment',
        pkceMethod: 'S256',
        scope: environment.keycloak.scope,
        redirectUri: resolveAuthUrl(environment.keycloak.redirectUri),
        silentCheckSsoRedirectUri:
          enableSsoCheck && enableSilentSso
            ? buildSilentCheckSsoRedirectUri()
            : undefined,
        silentCheckSsoFallback: false,
        checkLoginIframe: allowIframeSessionChecks,
        checkLoginIframeInterval: 5,
        enableLogging: !environment.production,
      };

      const authenticated = await keycloak.init(initOptions);

      this.isAuthenticatedSubject.next(authenticated);
      if (authenticated) {
        this.persistTokens(keycloak);
        this.persistLastActivity();
        this.resetIdleTimer();
        void this.getUserInfo();
      } else {
        this.clearSessionIdleWarning();
      }

      return authenticated;
    } finally {
      this.initialLoadCompleted = true;
      this.readySubject.next(true);
    }
  }

  private bindEventHandlers(keycloak: Keycloak): void {
    keycloak.onReady = (authenticated) => {
      this.isAuthenticatedSubject.next(Boolean(authenticated));
    };

    keycloak.onAuthSuccess = () => {
      this.isAuthenticatedSubject.next(true);
      this.persistTokens(keycloak);
      this.persistLastActivity();
      this.resetIdleTimer();

      if (this.initialLoadCompleted) {
        this.userInfoCache = null;
        void this.getUserInfo();
      }
    };

    keycloak.onAuthRefreshSuccess = () => {
      this.isAuthenticatedSubject.next(Boolean(keycloak.authenticated));
      this.persistTokens(keycloak);
    };

    keycloak.onAuthLogout = () => {
      const wasAuthenticated = this.isAuthenticatedSubject.value;

      this.isAuthenticatedSubject.next(false);
      this.userProfile = null;
      this.userInfoCache = null;
      this.refreshTokenExpiresAt = null;
      this.refreshTokenLifetimeSeconds = null;
      this.tokenRefreshInFlight = null;
      this.lastTokenRefreshAttemptAt = 0;
      this.clearIdleTimers();
      this.clearSessionIdleWarning();
      this.clearStoredTokens();
      this.clearStoredUserInfo();

      if (
        this.initialLoadCompleted &&
        wasAuthenticated &&
        !this.explicitLogoutRequested
      ) {
        this.sessionTerminatedSubject.next();
      }

      this.explicitLogoutRequested = false;
    };

    keycloak.onAuthRefreshError = () => {
      this.refreshTokenExpiresAt = null;
      this.refreshTokenLifetimeSeconds = null;
      this.tokenRefreshInFlight = null;
      this.lastTokenRefreshAttemptAt = 0;
      this.clearStoredTokens();
      keycloak.clearToken();
      this.clearSessionIdleWarning();
    };

    keycloak.onTokenExpired = () => {
      // Do not refresh here: doing so would restart `refresh_expires_in` at
      // `expires_in` and prevent the inactivity warning from ever appearing.
      // API requests and explicit user activity refresh the token when needed.
    };
  }

  private requireKeycloak(): Keycloak {
    if (!this.keycloak) {
      throw new Error('Keycloak has not been initialized');
    }

    return this.keycloak;
  }

  /**
   * Computes the refresh token's real, absolute expiry moment and stores it
   * in-memory as refreshTokenExpiresAt.
   */
  private captureRefreshTokenExpiry(keycloak: Keycloak): void {
    const timeSkew = keycloak.timeSkew ?? 0;

    if (
      keycloak.refreshTokenParsed &&
      typeof keycloak.refreshTokenParsed.exp === 'number'
    ) {
      const issuedAt = keycloak.refreshTokenParsed.iat;
      this.refreshTokenLifetimeSeconds =
        typeof issuedAt === 'number'
          ? Math.max(0, keycloak.refreshTokenParsed.exp - issuedAt)
          : Math.max(
              0,
              keycloak.refreshTokenParsed.exp -
                Math.floor(Date.now() / 1000) -
                timeSkew,
            );
      this.refreshTokenExpiresAt =
        (keycloak.refreshTokenParsed.exp - timeSkew) * 1000;
      return;
    }

    const fallbackSeconds = (
      keycloak.tokenParsed as { refresh_expires_in?: number } | undefined
    )?.refresh_expires_in;
    if (typeof fallbackSeconds === 'number') {
      this.refreshTokenLifetimeSeconds = Math.max(0, fallbackSeconds);
      this.refreshTokenExpiresAt = Date.now() + fallbackSeconds * 1000;
      return;
    }

    this.refreshTokenExpiresAt = null;
    this.refreshTokenLifetimeSeconds = null;
  }

  private async refreshAccessToken(
    minValiditySeconds: number,
    force = false,
  ): Promise<string | null> {
    await this.initialize();

    const keycloak = this.keycloak;
    if (!keycloak?.authenticated) {
      return null;
    }

    if (!keycloak.refreshToken) {
      const token = keycloak.token ?? null;
      if (token && !keycloak.isTokenExpired(minValiditySeconds)) {
        return token;
      }

      this.clearStoredTokens();
      keycloak.clearToken();
      return null;
    }

    const now = Date.now();
    const token = keycloak.token ?? null;
    const tokenStillValid =
      !!token && !keycloak.isTokenExpired(minValiditySeconds);
    const withinThrottleWindow =
      !force &&
      this.lastTokenRefreshAttemptAt > 0 &&
      now - this.lastTokenRefreshAttemptAt < this.tokenRefreshMinIntervalMs;

    if (this.tokenRefreshInFlight) {
      return this.tokenRefreshInFlight;
    }

    if (!force && withinThrottleWindow && tokenStillValid) {
      return token;
    }

    const refreshPromise = (async () => {
      try {
        this.lastTokenRefreshAttemptAt = Date.now();
        await keycloak.updateToken(minValiditySeconds);
        const refreshedToken = keycloak.token ?? null;

        if (refreshedToken) {
          this.persistTokens(keycloak);
        }

        return refreshedToken;
      } catch {
        this.clearStoredTokens();
        keycloak.clearToken();
        return null;
      } finally {
        if (this.tokenRefreshInFlight === refreshPromise) {
          this.tokenRefreshInFlight = null;
        }
      }
    })();

    this.tokenRefreshInFlight = refreshPromise;
    return refreshPromise;
  }

  private persistTokens(keycloak: Keycloak): void {
    this.captureRefreshTokenExpiry(keycloak);

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const token = keycloak.token;
    const refreshToken = keycloak.refreshToken;

    if (!token || !refreshToken) {
      this.clearStoredTokens();
      return;
    }

    const refreshExpiresIn = this.refreshTokenExpiresAt
      ? Math.max(
          0,
          Math.round((this.refreshTokenExpiresAt - Date.now()) / 1000),
        )
      : undefined;

    const payload = {
      token,
      refreshToken,
      idToken: keycloak.idToken,
      timeSkew: keycloak.timeSkew,
      refresh_expires_in: refreshExpiresIn,
    };

    window.sessionStorage.setItem(
      this.tokenStorageKey,
      JSON.stringify(payload),
    );
  }

  private readStoredTokens(): {
    token?: string;
    refreshToken?: string;
    idToken?: string;
    timeSkew?: number;
    refresh_expires_in?: number;
  } | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    const raw = window.sessionStorage.getItem(this.tokenStorageKey);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as {
        token?: unknown;
        refreshToken?: unknown;
        idToken?: unknown;
        timeSkew?: unknown;
        refresh_expires_in?: unknown;
      };

      const token = typeof parsed.token === 'string' ? parsed.token : undefined;
      const refreshToken =
        typeof parsed.refreshToken === 'string'
          ? parsed.refreshToken
          : undefined;

      if (!token || !refreshToken) {
        window.sessionStorage.removeItem(this.tokenStorageKey);
        return null;
      }

      return {
        token,
        refreshToken,
        idToken:
          typeof parsed.idToken === 'string' ? parsed.idToken : undefined,
        timeSkew:
          typeof parsed.timeSkew === 'number' ? parsed.timeSkew : undefined,
        refresh_expires_in:
          typeof parsed.refresh_expires_in === 'number'
            ? parsed.refresh_expires_in
            : undefined,
      };
    } catch {
      window.sessionStorage.removeItem(this.tokenStorageKey);
      return null;
    }
  }

  private clearStoredTokens(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    window.sessionStorage.removeItem(this.tokenStorageKey);
    // Remove credentials written by older builds during the storage migration.
    window.localStorage.removeItem(this.tokenStorageKey);
    window.localStorage.removeItem('logto_refresh_token');
    window.localStorage.removeItem(this.idleActivityStorageKey);
    window.localStorage.removeItem('cached_menu_items');
    window.localStorage.removeItem('cached_menu_items_v2');
    window.localStorage.removeItem('user_permissions');
    window.localStorage.removeItem('user_permissions_hash');
    window.sessionStorage.removeItem(this.loginHintStorageKey);
    window.sessionStorage.removeItem(this.skipAutoLoginStorageKey);
    this.clearAllCookies();
  }

  private persistUserInfo(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const userInfoData: {
      profile?: KeycloakProfile;
      userInfo?: KeycloakUserInfo;
    } = {};

    if (this.userProfile) {
      userInfoData.profile = this.userProfile;
    }

    if (this.userInfoCache) {
      this.userInfoCache
        .then((userInfo) => {
          if (userInfo) {
            userInfoData.userInfo = userInfo;
            window.localStorage.setItem(
              this.userInfoStorageKey,
              JSON.stringify(userInfoData),
            );
          }
        })
        .catch(() => {
          if (this.userProfile) {
            window.localStorage.setItem(
              this.userInfoStorageKey,
              JSON.stringify(userInfoData),
            );
          }
        });
    } else if (this.userProfile) {
      window.localStorage.setItem(
        this.userInfoStorageKey,
        JSON.stringify(userInfoData),
      );
    }
  }

  private markSkipAutoLoginOnce(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    window.sessionStorage.setItem(this.skipAutoLoginStorageKey, '1');
  }

  private isAuthUtilityRoute(url: string): boolean {
    return (
      url.startsWith('/callback') ||
      url.startsWith('/auth-error') ||
      url.startsWith('/sessions/callback') ||
      url.startsWith('/sessions/auth-error')
    );
  }

  /** Accept only normalized, same-origin application routes. */
  private getSafeAppRoute(value: string): string | null {
    if (
      !value.startsWith('/') ||
      value.startsWith('//') ||
      value.startsWith('/\\')
    ) {
      return null;
    }

    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin) {
        return null;
      }

      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return null;
    }
  }

  private getSafeSameOriginUrl(value: string): string {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) {
      throw new Error('Keycloak post-logout redirect must be same-origin');
    }

    return url.toString();
  }

  /** Listen for an explicit logout signal from another tab. */
  private listenForCrossTabLogout(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === this.idleActivityStorageKey && event.newValue) {
        this.resetIdleTimer();
        // Activity is shared across tabs, but Keycloak tokens are kept in
        // sessionStorage and are therefore tab-specific. Refresh this tab's
        // token too so an inactive tab cannot expire and log out the active
        // browser session.
        this.extendSessionOnActivity();
      }

      if (event.key === this.logoutEventStorageKey && event.newValue) {
        this.clearIdleTimers();
        this.clearSessionIdleWarning();
        this.clearStoredTokens();
        this.markSkipAutoLoginOnce();
        this.clearStoredUserInfo();
        this.keycloak?.clearToken();
        this.isAuthenticatedSubject.next(false);
        this.userProfile = null;
        this.userInfoCache = null;
        this.refreshTokenExpiresAt = null;
        this.refreshTokenLifetimeSeconds = null;
        this.tokenRefreshInFlight = null;
        this.lastTokenRefreshAttemptAt = 0;
        window.location.href = '/';
      }
    });
  }

  private broadcastLogout(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    window.localStorage.setItem(
      this.logoutEventStorageKey,
      `${Date.now()}-${Math.random()}`,
    );
  }

  private persistLastActivity(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!this.keycloak?.authenticated) {
      return;
    }

    window.localStorage.setItem(
      this.idleActivityStorageKey,
      Date.now().toString(),
    );
  }

  private resetIdleTimer(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.clearIdleTimers();

    if (!this.keycloak?.authenticated) {
      return;
    }

    const remainingSeconds = this.getRefreshTokenRemainingSeconds();
    const warningSeconds = this.getRefreshTokenExpiryWarningSeconds();

    if (remainingSeconds === null) {
      return;
    }

    if (remainingSeconds <= 0) {
      void this.logout(false).catch((e) =>
        console.error('Automatic logout after refresh token expiry failed:', e),
      );
      return;
    }

    const warningDelaySeconds = Math.max(0, remainingSeconds - warningSeconds);
    this.sessionIdleWarningTimeoutId = window.setTimeout(() => {
      const currentRemainingSeconds = this.getRefreshTokenRemainingSeconds();
      if (currentRemainingSeconds === null) {
        return;
      }

      if (currentRemainingSeconds <= 0) {
        void this.logout(false).catch((e) =>
          console.error('Automatic logout after refresh token expiry failed:', e),
        );
        return;
      }

      this.openSessionIdleWarningDialog(
        Math.min(Math.max(1, Math.ceil(currentRemainingSeconds)), warningSeconds),
      );
    }, Math.max(1000, warningDelaySeconds * 1000));
  }

  private startIdleMonitor(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.activityEvents.forEach((eventName) =>
      window.addEventListener(eventName, this.onUserActivity, {
        passive: true,
      }),
    );

    this.persistLastActivity();
    this.resetIdleTimer();
  }

  private openSessionIdleWarningDialog(countdownSeconds: number): void {
    if (!isPlatformBrowser(this.platformId) || !this.keycloak?.authenticated) {
      return;
    }

    if (this.sessionIdleDialogRef) {
      return;
    }

    const safeCountdownSeconds = Math.max(1, Math.floor(countdownSeconds));

    this.sessionIdleDialogRef = this.dialog.open(AppComfirmLogoutComponent, {
      width: '420px',
      disableClose: true,
      data: {
        title: 'Session expiring soon',
        message: 'Your Keycloak session is about to expire.',
        info: 'Stay signed in to refresh your session, or sign out now.',
        countdownSeconds: safeCountdownSeconds,
        continueLabel: 'Stay signed in',
        logoutLabel: 'Sign out now',
      },
    });

    this.sessionIdleDialogRef.beforeClosed().subscribe((result) => {
      this.sessionIdleDialogRef = null;

      if (result === true) {
        this.clearSessionIdleLogoutTimeout();
        this.persistLastActivity();
        void this.forceRefreshToken().finally(() => {
          this.resetIdleTimer();
        });
      } else if (result === false) {
        this.clearSessionIdleLogoutTimeout();
        void this.logout(false).catch((e) =>
          console.error('Logout from refresh token expiry dialog failed:', e),
        );
      }
    });

    if (this.sessionIdleLogoutTimeoutId !== null) {
      window.clearTimeout(this.sessionIdleLogoutTimeoutId);
    }

    this.sessionIdleLogoutTimeoutId = window.setTimeout(() => {
      this.closeSessionIdleWarningDialog();
      void this.logout(false).catch((e) =>
        console.error('Automatic logout after refresh token expiry failed:', e),
      );
    }, safeCountdownSeconds * 1000);
  }

  /**
   * Called on every throttled user-activity tick.
   */
  private extendSessionOnActivity(): void {
    if (!isPlatformBrowser(this.platformId) || !this.keycloak?.authenticated) {
      return;
    }

    const now = Date.now();
    const expiryWarningIsOpen = this.sessionIdleDialogRef !== null;
    if (
      !expiryWarningIsOpen &&
      now - this.lastActivityRefreshAt < this.activityRefreshMinIntervalMs
    ) {
      return;
    }

    this.lastActivityRefreshAt = now;

    void this.refreshAccessToken(-1)
      .then((token) => {
        if (token) {
          this.closeSessionIdleWarningDialog();
          this.resetIdleTimer();
        }
      })
      .catch(() => undefined);
  }

  private closeSessionIdleWarningDialog(): void {
    if (this.sessionIdleDialogRef) {
      this.sessionIdleDialogRef.close();
      this.sessionIdleDialogRef = null;
    }

    this.clearSessionIdleLogoutTimeout();
  }

  private clearSessionIdleLogoutTimeout(): void {
    if (this.sessionIdleLogoutTimeoutId !== null) {
      window.clearTimeout(this.sessionIdleLogoutTimeoutId);
      this.sessionIdleLogoutTimeoutId = null;
    }
  }

  private clearSessionIdleWarning(): void {
    this.closeSessionIdleWarningDialog();

    if (this.sessionIdleWarningTimeoutId !== null) {
      window.clearTimeout(this.sessionIdleWarningTimeoutId);
      this.sessionIdleWarningTimeoutId = null;
    }
  }

  private getRefreshTokenExpiryWarningSeconds(): number {
    return Math.max(
      1,
      environment.keycloak.refreshTokenExpiryWarningSeconds ?? 30,
    );
  }

  private getRefreshTokenRemainingSeconds(): number | null {
    if (this.refreshTokenLifetimeSeconds === null) {
      return null;
    }

    const storedActivity = Number(
      window.localStorage.getItem(this.idleActivityStorageKey),
    );
    const lastActivityAt =
      Number.isFinite(storedActivity) && storedActivity > 0
        ? storedActivity
        : Date.now();
    const idleSeconds = (Date.now() - lastActivityAt) / 1000;

    return Math.max(0, this.refreshTokenLifetimeSeconds - idleSeconds);
  }

  private clearIdleTimers(): void {
    this.clearSessionIdleWarning();
  }

  private clearAllCookies(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    document.cookie.split(';').forEach((cookie) => {
      const eqPos = cookie.indexOf('=');
      const name =
        eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
      if (name) {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
      }
    });
  }
}
